import { Location } from '@angular/common';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { EMPTY, Observable } from 'rxjs';
import { throwError } from 'rxjs/internal/observable/throwError';
import { catchError, take } from 'rxjs/operators';

import { MessagePopupComponent } from '../../popups/message-popup/message-popup.component';
import { PopupFactoryService } from '../../popups/popup-factory.service';
import { getDataFromLocalStorage } from '../../shared/helpers/other';
import { UserModel } from '../../shared/models';
import { Logout } from '../../store/app.actions';
import { SessionCheckerService } from '../session-checker.service';

import { SKIP_INTERCEPTOR } from './skip-interceptor.token';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(
    private store: Store,
    private router: Router,
    private popup: PopupFactoryService,
    private location: Location,
    private sessionCheck: SessionCheckerService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.context.get(SKIP_INTERCEPTOR)) {
      return next.handle(request);
    }
    const currentUser: UserModel = this.store.selectSnapshot(({ app }) => app.currentUser);
    const storedUser: UserModel | null = getDataFromLocalStorage('user');
    // Another tab logged in as a different user: reload so this tab doesn't act as them with stale UI.
    if (currentUser?.uid && storedUser?.uid && currentUser.uid !== storedUser.uid) {
      window.location.reload();
      return EMPTY;
    }
    let requestToken: string | null = null;
    // Keep an explicit Authorization header: login passes the token it is signing in with.
    if (currentUser && currentUser.token && !request.headers.has('Authorization')) {
      requestToken = this.getLatestToken();
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${requestToken}`,
        },
      });

      if (request.url !== '/version/') this.sessionCheck.pushRequest();
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if ((error.status === 401 && currentUser) || error.message === 'Token has expired') {
          const latestToken = this.getLatestToken();
          if (requestToken && latestToken && requestToken !== latestToken) {
            return next.handle(
              request.clone({
                setHeaders: {
                  Authorization: `Bearer ${latestToken}`,
                },
              }),
            );
          }

          this.store.dispatch(new Logout());
        }
        if (error.status === 403) {
          this.router.navigate(['/dashboard']);
        }
        if (error.status === 400 && !request?.url.includes('version')) {
          this.router.navigate(['/dashboard/400']);
        }
        if (error.status === 404) {
          if (
            (request.url.includes('api/users') && request.url.includes('attachment')) ||
            request.url.includes('medical-application') ||
            request.url.includes('api/users/signed')
          ) {
            this.popup
              .createPopup({
                popupComponent: MessagePopupComponent,
                preventBgClick: true,
                popupData: {
                  message: 'You have a broken link or the employee (document) has been removed',
                  yellowHeader: true,
                  closeRoute: null,
                },
              })
              .pipe(take(1))
              .subscribe(() => {
                this.location.back();
              });
          } else {
            this.router.navigate(['/dashboard/404']);
          }
        }
        if (error.status === 406) {
          if (request.url.includes('api/users/single')) {
            this.popup
              .createPopup({
                popupComponent: MessagePopupComponent,
                preventBgClick: true,
                popupData: {
                  message: 'You have a broken link or the employee (document) has been removed',
                  yellowHeader: true,
                  closeRoute: null,
                },
              })
              .pipe(take(1))
              .subscribe(() => {
                this.location.back();
              });
          }
        }
        if (error.status >= 500 && error.status <= 599 && !request?.url.includes('version')) {
          this.router.navigate(['/dashboard/500']);
        }
        if (error.status === 429) {
          this.router.navigate(['/dashboard/429']);
        }
        return throwError(() => error);
      }),
    );
  }

  // Prefer the token in localStorage: it is shared across tabs and holds the latest login or refresh.
  private getLatestToken(): string | null {
    const storedToken: string | undefined = getDataFromLocalStorage('user')?.token;
    if (storedToken) {
      return storedToken;
    }

    const state = this.store.selectSnapshot(({ app }) => ({
      currentUserToken: app.currentUser?.token,
      oldToken: app.oldToken,
      newToken: app.newToken,
    }));
    if (!state.currentUserToken) {
      return null;
    }
    return state.oldToken !== state.newToken ? state.newToken : state.currentUserToken;
  }
}
